import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";


import {
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  Paper,
  Stack,
  InputAdornment,
  IconButton,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import GoogleIcon from "@mui/icons-material/Google";
import FacebookIcon from "@mui/icons-material/Facebook";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { useAuth } from "../../context/AuthContext";
import Logo from "../assets/logo.png";

const LoginSignup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const from = location.state?.from?.pathname || "/";

  const [showPassword, setShowPassword] = React.useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ mode: "onChange" });

  const onSubmit = async (data) => {
    await new Promise((r) => setTimeout(r, 900));

    login({
      name: data.email.split("@")[0],
      email: data.email,
    });

    /*
      ONLY redirect to payout if:
      1. user came from payout flow
      2. proceed_to_payout flag is true
    */
    const shouldGoToPayout =
      localStorage.getItem("proceed_to_payout") === "true" &&
      from === "/payout";

    // always reset immediately after login
    localStorage.setItem("proceed_to_payout", "false");
    if (shouldGoToPayout) {
      navigate("/payout", { replace: true });
    } else {
      console.log("FROM =", from);
      navigate(from, { replace: true });
    }
  };



  useEffect(() => {

    document.title =
      "Login/Signup | Tecniqa";

  }, []);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f2f3ef",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        px: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 520,
          borderRadius: "24px",
          px: { xs: 3, sm: 5 },
          py: 5,
          position: "relative",
          bgcolor: "#fbfbf7",
          border: "1px solid #d6d2c4",
          boxShadow: "0 10px 40px rgba(31,42,26,0.10)",
        }}
      >
        {/* BACK BUTTON */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/")}
          sx={{
            position: "absolute",
            top: 18,
            left: 18,
            textTransform: "none",
            color: "#2f4a32",
            fontWeight: 600,
            fontSize: 13,
          }}
        >
          Back
        </Button>

        {/* HEADER */}
        <Stack alignItems="center" spacing={1}>
          <Box component="img" src={Logo} alt="Tecniqa" sx={{ width: 160 }} />

          <Typography
            sx={{
              fontSize: 30,
              fontWeight: 800,
              color: "#1f2a1a",
            }}
          >
            Welcome back
          </Typography>

          <Typography
            sx={{
              color: "#6f8572",
              fontSize: 14,
              textAlign: "center",
            }}
          >
            {from === "/payout"
              ? "Sign in to complete your order"
              : "Sign in to access your industrial dashboard"}
          </Typography>
        </Stack>

        {/* FORM */}
        <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 3 }}>
          {/* EMAIL */}
          <TextField
            fullWidth
            placeholder="Email address"
            error={!!errors.email}
            helperText={errors.email?.message}
            {...register("email", {
              required: "Email is required",
            })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlinedIcon sx={{ fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                height: 56,
                borderRadius: "12px",
                bgcolor: "#fff",
              },
            }}
          />

          {/* PASSWORD */}
          <TextField
            fullWidth
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            error={!!errors.password}
            helperText={errors.password?.message}
            {...register("password", {
              required: "Password is required",
            })}
            sx={{
              mb: 1,
              "& .MuiOutlinedInput-root": {
                height: 56,
                borderRadius: "12px",
                bgcolor: "#fff",
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((p) => !p)}
                    edge="end"
                  >
                    {showPassword ? (
                      <VisibilityOffOutlinedIcon />
                    ) : (
                      <VisibilityIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* FORGOT */}
          <Typography
            onClick={() => navigate("/forgot-password")}
            sx={{
              fontSize: 13,
              color: "#c8a84b",
              fontWeight: 600,
              cursor: "pointer",
              textAlign: "right",
              mb: 2,
            }}
          >
            Forgot password?
          </Typography>

          {/* SUBMIT */}
          <Button
            type="submit"
            fullWidth
            disabled={isSubmitting}
            sx={{
              bgcolor: "#2f4a32",
              py: 1.6,
              borderRadius: "12px",
              fontWeight: 700,
              textTransform: "none",
              color: "#fff",

              "&:hover": {
                bgcolor: "#223624",
              },
            }}
          >
            {isSubmitting ? "Signing in..." : "Sign in"}
          </Button>
        </Box>

        {/* DIVIDER */}
        <Divider sx={{ my: 3, color: "#6f8572" }}>
          OR
        </Divider>

        {/* GOOGLE */}
        <Button
          fullWidth
          startIcon={<GoogleIcon />}
          sx={{
            mb: 1.5,
            border: "1px solid #d6d2c4",
            bgcolor: "#fff",
            textTransform: "none",
            color: "#1f2a1a",
            fontWeight: 600,

            "&:hover": {
              bgcolor: "#f5f0e8",
            },
          }}
        >
          Sign in with Google
        </Button>

        {/* FACEBOOK */}
        <Button
          fullWidth
          startIcon={<FacebookIcon />}
          sx={{
            border: "1px solid #d6d2c4",
            bgcolor: "#fff",
            textTransform: "none",
            color: "#1f2a1a",
            fontWeight: 600,

            "&:hover": {
              bgcolor: "#f5f0e8",
            },
          }}
        >
          Sign in with Facebook
        </Button>

        {/* SIGN UP */}
        <Typography
          sx={{
            mt: 3,
            textAlign: "center",
            fontSize: 13,
            color: "#6f8572",
          }}
        >
          Don't have an account?{" "}
          <span
            onClick={() => navigate("/signup")}
            style={{
              color: "#c8a84b",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Sign up
          </span>
        </Typography>
      </Paper>
    </Box>
  );
};

export default LoginSignup;